<template>
  <template v-if="isPageContentHidden">
    <SuccessVerifyEmailModal v-if="isSuccessVerifyEmailModalOpen" />

    <AlreadyVerifiedEmailModal v-else-if="isAlreadyVerifiedEmailModalOpen" />
  </template>

  <template v-else>
    <slot></slot>
  </template>
</template>

<script setup>
import { onMounted, ref } from 'vue';
import SuccessVerifyEmailModal from '@/components/email/modal/SuccessVerifyEmailModal.vue';
import AlreadyVerifiedEmailModal from '@/components/email/modal/AlreadyVerifiedEmailModal.vue';
import useAuthStore from '@/store/modules/auth.js';
import usePageNavigationHook from '@/hooks/usePageNavigation.js';
import useModalHook from '@/hooks/useModal.js';
import useExecutionChainHook from '@/hooks/useExecutionChain.js';
import { apiGetEmailVerificationStatus } from '@/services/user.js';

const emits = defineEmits(['ready']);

const props = defineProps({
  performEmits: {
    type: Boolean,
    default: false,
  },
});

const authStore = useAuthStore();

const { redirectTo } = usePageNavigationHook();
const [isSuccessVerifyEmailModalOpen, openSuccessVerifyEmailModal] = useModalHook();
const [isAlreadyVerifiedEmailModalOpen, openAlreadyVerifiedEmailModal] = useModalHook();

const isPageContentHidden = ref(true);

const checkAndOpenEmailModal = async (executionChainController) => {
  const { checkId, email } = authStore.urlParams;

  if ([checkId, email].some(value => !value)) {
    executionChainController.next();

    return;
  }

  authStore.resetUrlParams(['checkId', 'email']);

  try {
    const afterHideCallback = () => {
      executionChainController.next();

      redirectTo(authStore.isUserAuthenticated ? 'Home' : 'Login');
    };

    const { isFirstTimeVerified } = await apiGetEmailVerificationStatus({ queryParams: { checkId, email } });

    if (isFirstTimeVerified) {
      openSuccessVerifyEmailModal({ afterHideCallback });
    } else {
      openAlreadyVerifiedEmailModal({ afterHideCallback });
    }
  } catch {
    executionChainController.next();
  }
};

const showPageContent = (_executionChainController) => {
  isPageContentHidden.value = false;

  if (props.performEmits) {
    emits('ready');
  }
};

onMounted(() => {
  const { executionChainController } = useExecutionChainHook([
    checkAndOpenEmailModal,
    showPageContent,
  ]);

  executionChainController.value.launch();
});
</script>
